export interface RowGroupMemberLike<TableId extends string = string> {
  tableId: TableId;
  rowIndex: number;
}

export interface RowGroupLike<TableId extends string = string> {
  members: Array<RowGroupMemberLike<TableId>>;
}

export interface TableMetadataLike<TableId extends string = string> {
  _id: TableId;
  selectedForSubmittal: boolean;
}

/**
 * Finds a column index by exact header name match.
 * Returns -1 when no candidate matches.
 */
export function findExactColumnIndex(
  headers: string[],
  candidates: string[]
): number {
  for (const name of candidates) {
    const idx = headers.indexOf(name);
    if (idx >= 0) {
      return idx;
    }
  }
  return -1;
}

/**
 * Finds a column index using fuzzy includes matching against normalized headers.
 * Returns 0 when no candidate matches to preserve current row-selection behavior.
 */
export function findFuzzyColumnIndex(
  headers: string[],
  candidates: string[]
): number {
  const normalized = headers.map((header) => header.toLowerCase().trim());
  for (const candidate of candidates) {
    const index = normalized.findIndex((header) => header.includes(candidate));
    if (index >= 0) {
      return index;
    }
  }
  return 0;
}

export function buildRowLabel(row: string[]): string {
  const primary = row[0]?.trim();
  const secondary = row[1]?.trim();
  if (primary && secondary) {
    return `${primary} - ${secondary}`;
  }
  if (primary) {
    return primary;
  }
  return "Untitled row";
}

/**
 * Collect table IDs that need hydrated row data:
 * - selected tables
 * - any table referenced by existing row groups
 */
export function collectHydratedTableIds<TableId extends string>(
  tables: Array<TableMetadataLike<TableId>>,
  rowGroups: Array<RowGroupLike<TableId>>
): TableId[] {
  const ids = new Set<TableId>();

  for (const table of tables) {
    if (table.selectedForSubmittal) {
      ids.add(table._id);
    }
  }

  for (const group of rowGroups) {
    for (const member of group.members) {
      ids.add(member.tableId);
    }
  }

  return [...ids];
}
